import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Check, ArrowRight } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { Reveal, Lines, EASE } from "../components/site/Motion";

const EMPTY = { first_name: "", last_name: "", email: "", phone: "", cap: "", province: "", privacy: false };

export default function FinancePage() {
  const [params] = useSearchParams();
  const [config, setConfig] = useState(null);
  const [selected, setSelected] = useState(params.get("offerta") === "b" ? "b" : "a");
  const [form, setForm] = useState(EMPTY);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    document.title = "Finanziamento SWAPA — Tua da €144,50/mese";
    api.get("/finance").then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  const offers = config?.offers || [];
  const offerA = offers.find((o) => o.id === "a");
  const offerB = offers.find((o) => o.id === "b");
  const current = useMemo(() => offers.find((o) => o.id === selected), [offers, selected]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.privacy) {
      setError("Il consenso al trattamento dei dati è obbligatorio per essere ricontattato.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/leads/finance", {
        ...form,
        finance_offer: selected === "a" ? "144_4000" : "202_zero",
      });
      setDone(true);
      toast.success("Richiesta inviata. Ti ricontatteremo a breve.");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full border border-white/15 bg-ink px-4 py-3.5 text-sm text-white placeholder:text-zinc-600 focus:border-swapa-yellow focus:outline-none";

  const OptionCard = ({ offer, n, testid }) => {
    if (!offer) return null;
    const isSel = selected === offer.id;
    return (
      <button
        data-testid={testid}
        onClick={() => setSelected(offer.id)}
        aria-pressed={isSel}
        className={`flex flex-col border p-8 text-left transition-all duration-300 sm:p-10 ${
          isSel ? "border-swapa-yellow bg-white/5" : "border-white/10 hover:border-white/30"
        }`}
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-zinc-500">Opzione {n}</span>
        <span className="mt-4 font-mono text-5xl font-semibold tracking-tighter text-white sm:text-6xl">€{offer.monthly}</span>
        <span className="mt-1 font-mono text-xs uppercase tracking-[0.2em] text-zinc-400">/ mese × {offer.rate}</span>
        <span className="mt-4 font-display text-lg font-bold uppercase tracking-tight text-swapa-yellow">{offer.anticipo_label}</span>
        <span className="mt-5 space-y-1.5 border-t border-white/10 pt-5 font-mono text-[11px] text-zinc-400">
          <span className="flex justify-between">Primo canone <b className="text-white">€{offer.anticipo}</b></span>
          <span className="flex justify-between">TAN fisso <b className="text-white">{offer.tan}</b></span>
          <span className="flex justify-between">TAEG <b className="text-white">{offer.taeg}</b></span>
          <span className="flex justify-between">Rata finale <b className="text-white">€{offer.rata_finale}</b></span>
        </span>
        {offer.refi && (
          <span className="mt-3 font-serif text-sm italic text-zinc-400">oppure {offer.refi} per rifinanziare la rata finale*</span>
        )}
        <span className={`mt-6 inline-flex w-fit items-center gap-2 px-5 py-2.5 font-mono text-[10px] font-semibold uppercase tracking-[0.18em] ${
          isSel ? "bg-swapa-yellow text-black" : "border border-white/25 text-white"
        }`}>
          {isSel ? "Selezionata" : "Seleziona"}
        </span>
      </button>
    );
  };

  return (
    <div data-testid="finance-page" className="bg-ink pb-28 pt-32 sm:pt-40">
      <div className="mx-auto max-w-[1200px] px-5 sm:px-10">
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-swapa-yellow">Swapa Zip — Finance</p>
        <h1 className="mt-6 font-display font-black uppercase leading-[0.9] tracking-tighter">
          <Lines animate="load" lines={["Scegli", "la tua rata."]} className="text-5xl text-white sm:text-7xl" />
        </h1>
        <Reveal delay={0.25}>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-zinc-400">
            Due modi per portare SWAPA in città. Stessa vettura, stessa dotazione completa:
            cambia solo come la paghi.
          </p>
        </Reveal>

        <div className="mt-14 grid gap-6 lg:grid-cols-2">
          <Reveal><OptionCard offer={offerA} n="01" testid="finance-option-a" /></Reveal>
          <Reveal delay={0.12}><OptionCard offer={offerB} n="02" testid="finance-option-b" /></Reveal>
        </div>

        {done ? (
          <motion.div
            data-testid="finance-success"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE }}
            className="mt-14 border border-swapa-yellow/40 bg-card p-10"
          >
            <span className="flex h-12 w-12 items-center justify-center bg-swapa-yellow"><Check className="h-6 w-6 text-black" /></span>
            <h2 className="mt-6 font-display text-2xl font-extrabold uppercase tracking-tight text-white">Richiesta inviata.</h2>
            <p className="mt-4 max-w-lg text-sm leading-relaxed text-zinc-400">
              Un consulente SWAPA ti ricontatterà per costruire la proposta personalizzata e
              verificare la documentazione necessaria. Nessun impegno fino alla firma del contratto.
            </p>
          </motion.div>
        ) : (
          current && (
            <Reveal delay={0.15}>
              <form onSubmit={submit} data-testid="finance-lead-form" className="mt-14 border border-white/10 bg-card p-6 sm:p-10">
                <h2 className="font-display text-2xl font-extrabold uppercase tracking-tight text-white">
                  Richiedi l'offerta — €{current.monthly}/mese
                </h2>
                <p className="mt-2 text-sm text-zinc-400">
                  Lascia i tuoi contatti: ti ricontattiamo noi. Nessun dato reddituale richiesto in questa fase.
                </p>
                <div className="mt-8 grid gap-4 sm:grid-cols-2">
                  <input data-testid="finance-firstname-input" required value={form.first_name} onChange={(e) => set("first_name", e.target.value)} placeholder="Nome *" className={inputCls} />
                  <input data-testid="finance-lastname-input" required value={form.last_name} onChange={(e) => set("last_name", e.target.value)} placeholder="Cognome *" className={inputCls} />
                  <input data-testid="finance-email-input" required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="Email *" className={inputCls} />
                  <input data-testid="finance-phone-input" required value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="Telefono *" className={inputCls} />
                  <input data-testid="finance-cap-input" value={form.cap} onChange={(e) => set("cap", e.target.value)} placeholder="CAP" className={inputCls} />
                  <input data-testid="finance-province-input" value={form.province} onChange={(e) => set("province", e.target.value)} placeholder="Provincia (es. MI)" className={inputCls} />
                </div>
                <label className="mt-6 flex cursor-pointer items-start gap-3 text-xs leading-relaxed text-zinc-400">
                  <input data-testid="finance-privacy-checkbox" type="checkbox" checked={form.privacy} onChange={(e) => set("privacy", e.target.checked)} className="mt-0.5 h-4 w-4 shrink-0 accent-[#FFD700]" />
                  Ho letto l'informativa privacy e acconsento al trattamento dei dati per essere ricontattato in merito all'offerta finanziaria. *
                </label>
                {error && <p data-testid="finance-form-error" className="mt-4 text-sm text-red-400">{error}</p>}
                <button
                  type="submit"
                  data-testid="finance-submit-button"
                  disabled={loading}
                  className="group mt-8 inline-flex items-center gap-3 bg-swapa-yellow px-8 py-4 font-mono text-xs font-semibold uppercase tracking-[0.18em] text-black transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-50"
                >
                  {loading ? "Invio in corso…" : "Richiedi l'offerta"}
                  <ArrowRight className="h-4 w-4" />
                </button>
              </form>
            </Reveal>
          )
        )}

        <Reveal delay={0.2}>
          <div data-testid="finance-legal" className="mt-14 space-y-4 border-t border-white/10 pt-8 text-[11px] leading-relaxed text-zinc-500">
            {offers.some((o) => o.legal?.trim()) ? (
              offers.map((o) => o.legal?.trim() && <p key={o.id}>{o.legal}</p>)
            ) : (
              <p>
                Messaggio pubblicitario con finalità promozionale. Offerte di finanziamento soggette ad
                approvazione e valutazione del merito creditizio da parte dell'istituto finanziatore.
                *Le condizioni di rifinanziamento della rata finale sono soggette a nuova approvazione.
                La documentazione di trasparenza completa — importo totale del credito, importo totale
                dovuto dal consumatore, spese ed esempio rappresentativo — è in corso di pubblicazione
                ed è disponibile presso il venditore.
              </p>
            )}
          </div>
        </Reveal>
      </div>
    </div>
  );
}
